import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import { BRAND, BRAND_DARK, BRAND_TINT, formatMoney } from './pdfBrand';

const money = (value) => Number(value || 0);

const buildSummary = (contracts) => ({
  totalContracts: contracts.length,
  totalAmount: contracts.reduce((sum, c) => sum + money(c.total_payable), 0),
  totalReceived: contracts.reduce((sum, c) => sum + money(c.payment_received), 0),
  totalBalance: contracts.reduce((sum, c) => sum + money(c.balance), 0),
});

const contractRow = (c) => ({
  'Agreement No.': c.agreement_no,
  Client: c.client_company_name,
  Status: (c.status || 'draft').toUpperCase(),
  'Campaign Start': c.campaign_start_date ? dayjs(c.campaign_start_date).format('YYYY-MM-DD') : '',
  'Campaign End': c.campaign_end_date ? dayjs(c.campaign_end_date).format('YYYY-MM-DD') : '',
  'Total Advertising Fee': money(c.total_advertising_fee),
  'VAT / Tax': money(c.vat_tax),
  'Total Payable': money(c.total_payable),
  'Payment Received': money(c.payment_received),
  Balance: money(c.balance),
});

/**
 * Exports every contract (already-fetched full list, not just one page) as
 * an Excel workbook, with a totals row summarizing the fee/received/balance
 * columns across all of them.
 */
export const exportContractsExcel = (contracts) => {
  const summary = buildSummary(contracts);
  const rows = contracts.map(contractRow);

  rows.push({
    'Agreement No.': 'TOTAL',
    Client: `${summary.totalContracts} contract(s)`,
    Status: '',
    'Campaign Start': '',
    'Campaign End': '',
    'Total Advertising Fee': '',
    'VAT / Tax': '',
    'Total Payable': summary.totalAmount,
    'Payment Received': summary.totalReceived,
    Balance: summary.totalBalance,
  });

  const worksheet = XLSX.utils.json_to_sheet(rows);
  worksheet['!cols'] = [
    { wch: 14 }, { wch: 26 }, { wch: 10 }, { wch: 13 }, { wch: 13 },
    { wch: 18 }, { wch: 12 }, { wch: 14 }, { wch: 16 }, { wch: 14 },
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Contracts');
  XLSX.writeFile(workbook, `contracts-report-${dayjs().format('YYYY-MM-DD')}.xlsx`);
};

/**
 * Exports every contract as a PDF report: a summary block (total contracts,
 * total amount, total received, total balance) followed by a table of every
 * contract.
 */
export const exportContractsPdf = (contracts) => {
  const summary = buildSummary(contracts);
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 12;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(...BRAND_DARK);
  doc.text('Contracts Report', margin, 15);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text(`Generated ${dayjs().format('DD MMM YYYY, HH:mm')}`, margin, 21);

  // Summary cards
  const cards = [
    { label: 'Total Contracts', value: String(summary.totalContracts) },
    { label: 'Total Amount', value: `THB ${formatMoney(summary.totalAmount)}` },
    { label: 'Total Received', value: `THB ${formatMoney(summary.totalReceived)}` },
    { label: 'Total Balance Due', value: `THB ${formatMoney(summary.totalBalance)}` },
  ];
  const cardWidth = (pageWidth - margin * 2 - 9) / 4;
  let cardX = margin;
  const cardY = 27;
  cards.forEach((card) => {
    doc.setFillColor(...BRAND_TINT);
    doc.roundedRect(cardX, cardY, cardWidth, 18, 1.5, 1.5, 'F');
    doc.setFillColor(...BRAND);
    doc.rect(cardX, cardY, 2, 18, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...BRAND_DARK);
    doc.text(card.label.toUpperCase(), cardX + 5, cardY + 6);
    doc.setFontSize(12);
    doc.setTextColor(40, 40, 40);
    doc.text(card.value, cardX + 5, cardY + 13.5);
    cardX += cardWidth + 3;
  });

  const tableRows = contracts.map((c) => [
    c.agreement_no,
    c.client_company_name,
    (c.status || 'draft').toUpperCase(),
    c.campaign_start_date ? dayjs(c.campaign_start_date).format('DD MMM YYYY') : '—',
    c.campaign_end_date ? dayjs(c.campaign_end_date).format('DD MMM YYYY') : '—',
    `THB ${formatMoney(c.total_payable)}`,
    `THB ${formatMoney(c.payment_received)}`,
    `THB ${formatMoney(c.balance)}`,
  ]);

  autoTable(doc, {
    startY: cardY + 24,
    margin: { left: margin, right: margin },
    head: [['Agreement No.', 'Client', 'Status', 'Campaign Start', 'Campaign End', 'Total Payable', 'Received', 'Balance']],
    body: tableRows,
    foot: [['', '', '', '', 'TOTAL', `THB ${formatMoney(summary.totalAmount)}`, `THB ${formatMoney(summary.totalReceived)}`, `THB ${formatMoney(summary.totalBalance)}`]],
    styles: { fontSize: 8.5, cellPadding: 2.5 },
    headStyles: { fillColor: BRAND, textColor: [255, 255, 255] },
    footStyles: { fillColor: [245, 245, 245], textColor: [40, 40, 40], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [252, 250, 247] },
    columnStyles: {
      5: { halign: 'right' },
      6: { halign: 'right' },
      7: { halign: 'right' },
    },
  });

  doc.save(`contracts-report-${dayjs().format('YYYY-MM-DD')}.pdf`);
};
